<template>
    <div class="line-chart-container">
        <highcharts 
            v-if="chartOptions" 
            :options="chartOptions" 
            class="w-full h-full"
        ></highcharts>
        <div v-else class="flex items-center justify-center h-full text-gray-500">
            <p>No data available</p>
        </div>
    </div>
</template>

<script>
import { Chart } from "highcharts-vue";

export default {
    name: "LineChart",
    components: {
        highcharts: Chart
    },
    props: {
        series: {
            type: Array,
            required: true,
            // Expected format: [
            //   { name: 'Contact Analytics', data: [75, 100, 80, ...] },
            //   { name: 'Data Import', data: [60, 50, 85, ...] }
            // ]
        },
        categories: {
            type: Array,
            default: () => ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
        },
        height: {
            type: Number,
            default: 300
        },
        showLegend: {
            type: Boolean,
            default: true
        },
        showGrid: {
            type: Boolean,
            default: true
        },
        categoryTitle: {
            type: String,
            default: 'Months of the year'
        }
    },
    computed: {
        chartOptions() {
            if (!this.series || this.series.length === 0) return null;

            return {
                chart: {
                    type: 'line',
                    backgroundColor: 'transparent',
                    height: this.height,
                    style: {
                        fontFamily: 'inherit'
                    }
                },
                title: {
                    text: null
                },
                credits: {
                    enabled: false
                },
                accessibility: {
                    enabled: true,
                    point: {
                        valueDescriptionFormat: '{xDescription}{separator}{value}'
                    }
                },
                legend: {
                    enabled: this.showLegend,
                    align: 'center',
                    verticalAlign: 'bottom',
                    layout: 'horizontal',
                    itemStyle: {
                        color: '#6B7280',
                        fontSize: '12px',
                        fontWeight: '500'
                    },
                    itemHoverStyle: {
                        color: '#111827'
                    },
                    itemMarginBottom: 5,
                    symbolRadius: 6,
                    symbolHeight: 10,
                    symbolWidth: 10,
                    symbolPadding: 8
                },
                xAxis: {
                    categories: this.categories,
                    gridLineWidth: 0,
                    lineColor: '#E5E7EB',
                    lineWidth: 1,
                    tickColor: '#E5E7EB',
                    tickWidth: 1,
                    tickLength: 5,
                    labels: {
                        style: {
                            color: '#6B7280',
                            fontSize: '12px',
                            fontWeight: '400'
                        },
                        y: 20
                    },
                    crosshair: {
                        width: 1,
                        color: '#E5E7EB',
                        dashStyle: 'Dash'
                    },
                    accessibility: {
                        description: this.categoryTitle
                    }
                },
                yAxis: {
                    title: {
                        text: null
                    },
                    gridLineColor: this.showGrid ? '#F3F4F6' : 'transparent',
                    gridLineWidth: 1,
                    gridLineDashStyle: 'Solid',
                    labels: {
                        style: {
                            color: '#6B7280',
                            fontSize: '12px',
                            fontWeight: '400'
                        },
                        x: -10
                    },
                    lineWidth: 0,
                    minorGridLineWidth: 0
                },
                tooltip: {
                    backgroundColor: 'rgba(55, 65, 81, 0.97)',
                    borderColor: '#374151',
                    borderRadius: 8,
                    borderWidth: 0,
                    shadow: {
                        color: 'rgba(0, 0, 0, 0.1)',
                        offsetX: 0,
                        offsetY: 2,
                        width: 8,
                        opacity: 0.15
                    },
                    style: {
                        color: '#ffffff',
                        fontSize: '12px',
                        fontWeight: '400'
                    },
                    padding: 12,
                    shared: true,
                    useHTML: true,
                    headerFormat: '<div style="font-size: 11px; color: #D1D5DB; margin-bottom: 6px;">{point.key}</div>',
                    pointFormat: '<div style="display: flex; align-items: center; margin-bottom: 4px;">' +
                                '<span style="color:{point.color}; font-size: 16px; margin-right: 6px;">●</span>' +
                                '<span style="font-weight: 500;">{series.name}:</span>' +
                                '<span style="margin-left: 8px; font-weight: 600;">{point.y}</span>' +
                                '</div>',
                    footerFormat: '</div>',
                    crosshairs: [{
                        width: 1,
                        color: '#9CA3AF',
                        dashStyle: 'Dash'
                    }, false]
                },
                plotOptions: {
                    line: {
                        lineWidth: 2.5,
                        fillOpacity: 0.15,
                        marker: {
                            enabled: true,
                            radius: 5,
                            symbol: 'circle',
                            lineWidth: 2,
                            lineColor: '#ffffff'
                        },
                        states: {
                            hover: {
                                lineWidth: 3,
                                halo: {
                                    size: 8,
                                    opacity: 0.25
                                }
                            }
                        },
                        dataLabels: {
                            enabled: false
                        }
                    },
                    area: {
                        lineWidth: 2.5,
                        fillOpacity: 0.15,
                        marker: {
                            enabled: true,
                            radius: 5,
                            symbol: 'circle',
                            lineWidth: 2,
                            lineColor: '#ffffff'
                        },
                        states: {
                            hover: {
                                lineWidth: 3,
                                halo: {
                                    size: 8,
                                    opacity: 0.25
                                }
                            }
                        }
                    },
                    series: {
                        animation: {
                            duration: 1200,
                            easing: 'easeOutQuart'
                        },
                        point: {
                            events: {
                                mouseOver: function() {
                                    this.graphic.attr({
                                        r: 6
                                    });
                                },
                                mouseOut: function() {
                                    this.graphic.attr({
                                        r: 4
                                    });
                                }
                            }
                        }
                    }
                },
                series: this.series
            };
        }
    }
};
</script>

<style scoped>
.line-chart-container {
    width: 100%;
    height: 100%;
}
</style>
